import type { PageDefinition } from '@/types/page'
import { CHANNEL_OPTIONS, DRUG_REVIEW_STATUS, toOptions } from './common'

const DRUG_CATEGORIES = ['解热镇痛', '抗感染', '降糖药', '心血管', '中成药', '维生素', '消化系统', '呼吸系统'].map(value => ({ value, label: value }))
const OTC_OPTIONS = [
  { value: '非处方药', label: '非处方药（OTC）' },
  { value: '处方药', label: '处方药（Rx）' },
]
const STOCK_STATE = [
  { value: 'sufficient', label: '库存充足', tone: 'success' as const },
  { value: 'low', label: '库存低', tone: 'warning' as const },
  { value: 'out', label: '已缺货', tone: 'danger' as const },
]
const DOSAGE_FORMS = ['片剂', '胶囊', '颗粒剂', '口服液', '注射剂', '软膏', '滴眼液'].map(value => ({ value, label: value }))

export const drugsPage: PageDefinition = {
  id: 'drugs',
  route: '#/drugs',
  title: '药品资料',
  purpose: '维护平台药品主数据、分类、规格、库存、条码与说明书，保障用药计划可选药品安全可用',
  group: '药品库',
  icon: 'drug',
  kind: 'table',
  permission: 'drugs.view',
  permissions: {
    create: 'drugs.create',
    edit: 'drugs.edit',
    stock: 'drugs.stock',
    submit: 'drugs.submit',
    review: 'drugs.review.view',
    import: 'drugs.import',
    export: 'drugs.export',
    category: 'drugs.category',
  },
  searchKeys: ['generic_name', 'trade_name', 'approval_no', 'barcode', 'manufacturer', 'category_name'],
  statusKey: 'review_status',
  statusMap: DRUG_REVIEW_STATUS,
  timeField: 'updated_at',
  summary: [
    { key: 'total', label: '药品总数', kind: 'count', hint: '平台在册药品主数据' },
    { key: 'approved', label: '已上架', kind: 'count', where: { review_status: 'approved' }, hint: '可用于用药计划' },
    { key: 'low', label: '低库存药品', kind: 'count', where: { stock_low: true }, hint: '低于安全库存' },
    { key: 'pending', label: '待审核药品', kind: 'count', where: { review_status: 'pending' }, hint: '等待药事审核' },
    { key: 'recent', label: '近7日新增', kind: 'count', where: { created_within_days: 7 }, hint: '本周新增主数据' },
  ],
  columns: [
    { key: 'generic_name', label: '药品名称', type: 'primary', subtitleKey: 'approval_no', minWidth: 206, sortable: true },
    { key: 'category_name', label: '分类', type: 'tag', width: 96 },
    { key: 'spec', label: '规格', minWidth: 100 },
    { key: 'indication', label: '适应症', minWidth: 176 },
    { key: 'unit', label: '单位', width: 54, align: 'center' },
    { key: 'stock', label: '库存状态', minWidth: 108 },
    { key: 'review_status', label: '审核状态', type: 'status', width: 90 },
    { key: 'updated_at', label: '最近更新', type: 'datetime', width: 156, sortable: true },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '药品名称、通用名、生产厂家或条形码', width: 260 },
    { key: 'category_name', label: '分类', type: 'select', optionSource: 'drug_categories', width: 120 },
    { key: 'otc_type', label: '处方类型', type: 'select', options: OTC_OPTIONS, width: 142 },
    { key: 'stock_state', label: '库存状态', type: 'select', options: STOCK_STATE, width: 116 },
    { key: 'review_status', label: '审核状态', type: 'select', options: toOptions(DRUG_REVIEW_STATUS), width: 116 },
  ],
  formFields: [
    { key: 'drug_no', label: '药品编号', type: 'readonly', readonlyOnEdit: true, help: '保存后由系统生成，不可修改', placeholder: '保存后自动生成' },
    { key: 'generic_name', label: '通用名称', type: 'text', required: true, maxLength: 60, placeholder: '如：布洛芬缓释胶囊' },
    { key: 'trade_name', label: '商品名称', type: 'text', required: true, maxLength: 60 },
    { key: 'approval_no', label: '批准文号', type: 'text', required: true, pattern: '^国药准字[HZSJBF]\\d{8}$', patternMessage: '请输入有效的国药准字批准文号', placeholder: '国药准字H20203456' },
    { key: 'barcode', label: '条形码', type: 'text', required: true, pattern: '^\\d{8,14}$', patternMessage: '请输入8-14位数字条形码' },
    { key: 'category_name', label: '药品分类', type: 'select', optionSource: 'drug_categories', required: true },
    { key: 'otc_type', label: '处方类型', type: 'select', options: OTC_OPTIONS, required: true },
    { key: 'dosage_form', label: '剂型', type: 'select', options: DOSAGE_FORMS, required: true },
    { key: 'spec', label: '规格', type: 'text', required: true, maxLength: 40, placeholder: '如：0.3g × 24粒' },
    { key: 'unit', label: '库存单位', type: 'select', options: ['盒', '瓶', '支', '袋', '板'].map(value => ({ value, label: value })), required: true },
    { key: 'manufacturer', label: '生产厂家', type: 'text', required: true, maxLength: 80 },
    { key: 'stock', label: '当前库存', type: 'number', required: true, min: 0, max: 999999, suffix: '单位' },
    { key: 'safety_stock', label: '安全库存', type: 'number', required: true, min: 0, max: 99999, suffix: '单位', help: '低于安全库存时进入补货提醒' },
    { key: 'indication', label: '适应症', type: 'textarea', required: true, full: true, maxLength: 300 },
    { key: 'usage', label: '用法用量', type: 'textarea', required: true, full: true, maxLength: 300 },
    { key: 'precautions', label: '注意事项', type: 'textarea', full: true, maxLength: 300 },
    { key: 'review_status', label: '审核状态', type: 'select', options: toOptions(DRUG_REVIEW_STATUS), defaultValue: 'pending' },
  ],
  createLabel: '新增药品',
  createPermission: 'drugs.create',
  editPermission: 'drugs.edit',
  exportPermission: 'drugs.export',
  importPermission: 'drugs.import',
  exportScope: 'filtered',
  extraActions: [
    { key: 'scan', label: '扫码录入', icon: 'scan', kind: 'action' },
    { key: 'review', label: '审核上架', icon: 'shield', kind: 'link', to: '#/drugs/review', permission: 'drugs.review.view' },
    { key: 'categories', label: '分类维护', icon: 'layers', kind: 'link', to: '#/drugs?panel=categories', permission: 'drugs.category' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'drugs.edit', tone: 'primary' },
    { key: 'adjust-stock', label: '调整库存', permission: 'drugs.stock', form: [
      { key: 'stock', label: '调整后库存', type: 'number', required: true, min: 0 },
      { key: 'reason', label: '调整原因', type: 'textarea', required: true, full: true },
    ] },
    { key: 'submit-review', label: '提交审核', permission: 'drugs.submit', confirm: true, visibleWhen: { field: 'review_status', in: ['rejected', 'offline'] } },
    { key: 'offline', label: '下架', permission: 'drugs.edit', tone: 'danger', confirm: true, visibleWhen: { field: 'review_status', in: ['approved'] } },
  ],
  batchActions: [
    { key: 'batch-review', label: '提交审核', permission: 'drugs.submit', kind: 'transition' },
    { key: 'batch-export', label: '导出当前筛选', permission: 'drugs.export', kind: 'export' },
  ],
  detailSections: [
    { title: '药品主数据', fields: ['drug_no', 'generic_name', 'trade_name', 'approval_no', 'barcode', 'category_name', 'otc_type', 'dosage_form', 'spec', 'unit'] },
    { title: '库存与生产', fields: ['stock', 'safety_stock', 'manufacturer', 'review_status', 'created_at', 'updated_at'] },
    { title: '说明书摘要', fields: ['indication', 'usage', 'precautions'] },
  ],
  detailMetrics: [
    { label: '当前库存', field: 'stock', type: 'number', suffix: ' 单位' },
    { label: '安全库存', field: 'safety_stock', type: 'number', suffix: ' 单位' },
    { label: '所属分类', field: 'category_name', type: 'text' },
  ],
  notice: '药品上架前必须完成药事审核；低于安全库存的药品会进入补货提醒，但不阻断已生效的用药计划。',
  emptyHint: '没有符合当前筛选条件的药品，可重置筛选后重试。',
  numberPrefix: 'YP',
  seedKey: 'drugs',
}

export const drugReviewPage: PageDefinition = {
  id: 'drug-review',
  route: '#/drugs/review',
  title: '上架审核',
  purpose: '对药品新增、变更和恢复上架执行药事审核，控制批准、驳回与下架状态',
  group: '药品库',
  icon: 'shield',
  kind: 'table',
  permission: 'drugs.review.view',
  permissions: {
    approve: 'drugs.review.approve',
    reject: 'drugs.review.reject',
  },
  searchKeys: ['drug_no', 'generic_name', 'trade_name', 'approval_no', 'manufacturer', 'category_name'],
  statusKey: 'review_status',
  statusMap: DRUG_REVIEW_STATUS,
  timeField: 'updated_at',
  summary: [
    { key: 'total', label: '审核记录', kind: 'count' },
    { key: 'pending', label: '待审核', kind: 'count', where: { review_status: 'pending' } },
    { key: 'approved', label: '已通过', kind: 'count', where: { review_status: 'approved' } },
    { key: 'rejected', label: '已驳回', kind: 'count', where: { review_status: 'rejected' } },
  ],
  columns: [
    { key: 'generic_name', label: '药品名称', type: 'primary', subtitleKey: 'drug_no', minWidth: 200, sortable: true },
    { key: 'approval_no', label: '批准文号', width: 164 },
    { key: 'manufacturer', label: '生产厂家', minWidth: 190 },
    { key: 'category_name', label: '分类', type: 'tag', width: 100 },
    { key: 'spec', label: '规格', width: 118 },
    { key: 'review_status', label: '审核状态', type: 'status', width: 90 },
    { key: 'updated_at', label: '提交时间', type: 'datetime', width: 156, sortable: true },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '药品名称、批准文号或生产厂家', width: 250 },
    { key: 'review_status', label: '审核状态', type: 'select', options: toOptions(DRUG_REVIEW_STATUS), width: 118 },
    { key: 'category_name', label: '分类', type: 'select', optionSource: 'drug_categories', width: 118 },
    { key: 'updated_at', label: '提交时间', type: 'daterange', timeField: 'updated_at', width: 230 },
  ],
  exportScope: 'none',
  rowActions: [
    { key: 'view', label: '审核详情' },
    {
      key: 'approve',
      label: '通过',
      permission: 'drugs.review.approve',
      tone: 'primary',
      confirm: true,
      confirmText: '通过后药品进入可选用状态，用药计划可引用该药品；请确认批准文号与说明书已核验。',
      visibleWhen: { field: 'review_status', in: ['pending', 'rejected'] },
    },
    {
      key: 'reject',
      label: '驳回',
      permission: 'drugs.review.reject',
      tone: 'danger',
      confirm: true,
      form: [{ key: 'reason', label: '驳回原因', type: 'textarea', required: true, full: true, placeholder: '说明资料缺失或不符项，原因将同步给提交人' }],
      visibleWhen: { field: 'review_status', in: ['pending'] },
    },
    { key: 'offline', label: '下架', permission: 'drugs.review.reject', tone: 'danger', confirm: true, visibleWhen: { field: 'review_status', in: ['approved'] } },
  ],
  batchActions: [
    { key: 'batch-approve', label: '批量通过', permission: 'drugs.review.approve', confirm: true, kind: 'transition' },
    { key: 'batch-reject', label: '批量驳回', permission: 'drugs.review.reject', tone: 'danger', confirm: true, kind: 'transition', form: [{ key: 'reason', label: '驳回原因', type: 'textarea', required: true, full: true }] },
  ],
  detailSections: [
    { title: '审核对象', fields: ['drug_no', 'generic_name', 'trade_name', 'approval_no', 'barcode', 'category_name', 'spec', 'manufacturer'] },
    { title: '说明书核验', fields: ['indication', 'usage', 'precautions', 'stock', 'safety_stock'] },
    { title: '审核状态', fields: ['review_status', 'created_at', 'updated_at'] },
  ],
  notice: '审核人不能批准自己提交的药品；通过、驳回与下架都会写入操作日志，并影响用药计划可选药品。',
  emptyHint: '当前没有待审核的药品。',
  seedKey: 'drugs',
}
